/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {Unit} from '@ion/core/src/types/entities';
import UnitChooser, {UnitOption} from './UnitChooser';

export interface UnitOfMeasureChooserProps {
  defaultValue?: Unit | undefined;
  readOnly?: boolean;
  onChange?: (val: string) => void;
  required?: boolean;
  error?: string;
  hasError?: boolean;
  isCtcAdmin?: boolean;
  useGroupDefaultOption?: boolean;
};

/**
 * @UnitOfMeasureChooser
 *
 * @param {boolean} hasError If the field has an error the value is passed on
 *   to the dropdown component
 * @param {string | object} defaultValue Default value passed to the component.
 *   Will only be considered if passed as UnitOfMeasureOption.
 * @param {boolean} readOnly If true then dropdown is disabled
 * @param {Function} onChange Callback fired with the selected value when a
 *   selection is made
 *
 * ```typescript
 * import {UnitOfMeasureChooser} from '@ion/dls/src/editors/';
 *
 * <UnitOfMeasureChooser defaultValue='Metric'
 *   hasError={true}
 *   readOnly={false}
 *   onChange={handleChange} />
 *
 * ```
 */
export const UnitOfMeasureChooser = ({
  hasError,
  defaultValue,
  readOnly,
  onChange,
  isCtcAdmin = false,
  useGroupDefaultOption = false
}: UnitOfMeasureChooserProps) => {
  const options: UnitOption[] = [{
    label : translate(`common:units.${!useGroupDefaultOption ? 'default' : 'groupDefault'}`),
    value : 'Default'
  }, {
    label : translate('common:units.us'),
    value : 'US'
  }, {
    label : translate('common:units.metric'),
    value : 'Metric'
  },
  {
    label : translate('common:units.uk'),
    value : 'Imperial'
  }];

  if (isCtcAdmin) {
    options.push(
      {
        label : translate('common:units.device'),
        value : 'Device'
      }, {
        label : translate('common:units.si'),
        value : 'SI'
      }
    );
  }

  return (
    <UnitChooser options={options}
      fieldLabel={translate('common:editors.unitOfMeasure.label')}
      hasError={hasError}
      defaultValue={defaultValue}
      readOnly={readOnly}
      onChange={onChange} />
  );
};

export default UnitOfMeasureChooser;
